<template>
  <base-section
    id="gallery"
    class="bg-gallery"
    space="56"
  >
    <!-- 移动端：原生横向滚动 tab 栏 -->
    <div
      v-if="isMobile && !loading"
      class="native-tab-bar"
    >
      <button
        v-for="(t, i) in tabs"
        :key="i"
        class="native-tab-item"
        :class="{ 'native-tab-item--active': tab === i }"
        @click="tab = i"
      >
        {{ t }}
      </button>
    </div>
    <!-- 桌面端：Vuetify v-tabs -->
    <v-tabs
      v-if="!isMobile && !loading"
      :key="$i18n.locale"
      v-model="tab"
      centered
      show-arrows
      class="mb-12"
    >
      <v-tab
        v-for="(t, i) in tabs"
        :key="i"
        :value="i"
        class="text-capitalize"
      >
        {{ t }}
      </v-tab>
    </v-tabs>

    <v-container style="max-width: 1160px">
      <v-row v-if="loading">
        <v-col
          cols="12"
          class="text-center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          />
          <p class="mt-4">
            {{ $t('common.loading') }}
          </p>
        </v-col>
      </v-row>
      <v-row v-else-if="filteredSolutions.length">
        <v-col
          v-for="solution in filteredSolutions"
          :key="solution.title"
          class="gallery-card"
          cols="12"
          sm="6"
          md="4"
        >
          <v-fade-transition>
            <solution-card
              v-bind="solution"
              :src="solution.path"
              :title="solution.title"
              :item-id="solution.slug || solution.id"
              :clickable="solution.clickable"
            />
          </v-fade-transition>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col
          cols="12"
          class="solutions-empty text-center py-12"
        >
          <p class="solutions-empty__message">
            These solutions are being upgraded. For specific business needs, please contact us for a one-on-one consultation with our expert team.
          </p>
          <v-btn
            class="solutions-empty__button"
            :to="{ name: 'Contact', params: { lang: $i18n.locale } }"
            color="primary"
            depressed
            rounded
          >
            CONTACT US
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </base-section>
</template>

<script>
  import solutionsData from '@/data/solutions.json'
  import { loadImage } from '@/utils/imageLoader'
  import SolutionCard from '@/components/SolutionCard'

  export default {
    name: 'SolutionsGallery',

    components: {
      SolutionCard,
    },

    data: () => ({
      tab: 0,
      solutions: [],
      categoryOptions: [],
      loading: true,
    }),

    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.width <= 960
      },
      tabs() {
        return [this.$t('solutions.gallery.all'), ...this.categoryOptions.map(item => item.label)]
      },

      activeCategoryValue() {
        if (this.tab <= 0) return ''
        const option = this.categoryOptions[this.tab - 1]
        return option?.value || ''
      },

      filteredSolutions() {
        return this.solutions.filter(solution => {
          if (!this.activeCategoryValue) return true
          return solution.category === this.activeCategoryValue
        })
      },
    },

    watch: {
      '$i18n.locale'() {
        this.loadSolutions()
      },
      // 已在 Solutions 页时通过 query 切换 tab
      '$route.query.category'(newVal) {
        if (!newVal) {
          this.tab = 0
          return
        }
        const idx = this.categoryOptions.findIndex(opt => opt.value === newVal)
        if (idx >= 0) this.tab = idx + 1
      },
      // tab 切换时同步更新 URL query 并通知标题变化
      tab(newVal) {
        const value = newVal === 0 ? undefined : (this.categoryOptions[newVal - 1]?.value)
        const currentCategory = this.$route.query.category
        if (value !== currentCategory) {
          this.$router.replace({ query: value ? { category: value } : {} }).catch(() => {})
        }
        this.notifyTitleChange()
      },
    },

    async mounted() {
      await this.loadSolutions()
    },

    methods: {
      // 向 HeroAlt 和 Solutions/Index 广播当前激活分类的显示名称
      notifyTitleChange() {
        const isAll = this.tab === 0
        const label = isAll
          ? this.$t('solutions.title')
          : (this.categoryOptions[this.tab - 1]?.label || this.$t('solutions.title'))
        this.$root.$emit('gallery-tab-changed', { label, isAll })
      },
      translateIfKey(value) {
        if (typeof value !== 'string') return value
        return this.$te(value) ? this.$t(value) : value
      },
      slugify(text) {
        if (!text) return ''
        return text
          .toString()
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .trim()
          .replace(/\s+/g, '-')
      },
      async loadSolutions() {
        this.loading = true
        try {
          await this.loadCategoryOptions()
          const resp = await this.$axios({
            method: 'get',
            url: '/public/solutions',
            params: { lang: this.$i18n.locale },
          })
          const cards = await this.buildCardsFromApi(resp)
          this.solutions = cards.length ? cards : await this.buildCardsFromLocal()
        } catch (error) {
          console.error('Failed to load solutions:', error)
          this.solutions = await this.buildCardsFromLocal()
        } finally {
          this.loading = false
          this.notifyTitleChange()
        }
      },
      async loadCategoryOptions() {
        const locale = (this.$i18n.locale || 'en').toLowerCase()
        let list = []
        try {
          const resp = await this.$axios({
            method: 'get',
            url: '/public/dict/data',
            params: { dictType: 'solutions_category' },
          })
          const active = Array.isArray(resp) ? resp.filter(item => item?.status === '0') : []
          if (active.length > 0) {
            list = active
          }
        } catch {
        }

        if (!Array.isArray(list) || list.length === 0) {
          this.categoryOptions = [
            { label: locale.startsWith('zh') ? '无人机' : 'Drone', value: 'Drone' },
            { label: locale.startsWith('zh') ? '配套硬件' : 'Related Hardware', value: 'Related Hardware' },
            { label: locale.startsWith('zh') ? '反无人机' : 'Anti-Drone', value: 'Anti-Drone' },
          ]
        } else {
          this.categoryOptions = list.map(item => ({
            label: item.dictLabel || item.dictValue,
            value: item.dictValue,
          }))
        }

        if (this.tab >= this.tabs.length) {
          this.tab = 0
        }

        // 如果 URL query 中有 category 参数，自动激活对应 tab
        const queryCategory = this.$route && this.$route.query && this.$route.query.category
        if (queryCategory) {
          const idx = this.categoryOptions.findIndex(opt => opt.value === queryCategory)
          if (idx >= 0) this.tab = idx + 1
        }
      },
      async buildCardsFromApi(solutions) {
        if (!Array.isArray(solutions) || !solutions.length) {
          return []
        }
        const cards = []
        const sortedSolutions = [...solutions]
          .filter(solution => solution && solution.visible !== false)
          .sort((a, b) => (Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)) || (Number(b?.id || 0) - Number(a?.id || 0)))
        for (const solution of sortedSolutions) {
          const title = this.translateIfKey(solution.name)
          const slug = solution.slug || `${solution.id}`
          const path = await loadImage(solution.coverImage || require('@/assets/logo/logo.png'))
          cards.push({
            id: solution.id,
            title,
            desc: solution.desc || solution.summary || '',
            path,
            slug,
            category: solution.category,
            clickable: solution.clickable !== false,
          })
        }
        return cards
      },
      async buildCardsFromLocal() {
        const localItems = Array.isArray(solutionsData?.solutions) ? solutionsData.solutions : []
        const solutions = []
        for (const solution of localItems) {
          try {
            const slug = `${solution.id}-${this.slugify(this.translateIfKey(solution.title))}`
            const path = await loadImage(solution.imgUrl)
            solutions.push({
              id: solution.id,
              title: this.translateIfKey(solution.title),
              desc: solution.desc,
              path,
              slug,
              category: solution.category,
              clickable: solution.clickable !== false,
            })
          } catch (imgError) {
            solutions.push({
              id: solution.id,
              title: this.translateIfKey(solution.title),
              desc: solution.desc,
              path: '',
              slug: `${solution.id}-${this.slugify(this.translateIfKey(solution.title))}`,
              category: solution.category,
              clickable: solution.clickable !== false,
            })
          }
        }
        return solutions
      },
    },
  }
</script>

<style lang="sass">
  .bg-gallery
    background: url('~@/assets/bg-w-1.png') no-repeat center center
  .gallery-card
    transform-origin: center center 0
    transition: .3s cubic-bezier(.25,.8,.50,1)
  .solutions-empty__message
    margin: 0 0 24px
    color: rgba(0, 0, 0, .6)
  .solutions-empty__button.v-btn
    min-width: 164px !important
    height: 48px !important
    padding: 0 28px !important
    letter-spacing: 0.5px

</style>
